import qs from "qs"

import type {
  FullEngagementInfo,
  NearbyDriversResponse,
  RegionExtended,
} from "@be-miniapp/types"

import { httpPost } from "../http"

export interface PointOfInterest {
  pid: string
  content: string
  address: string
  long: number
  lat: number
  is_nearby: boolean
  source?: string
}

export interface Session {
  title_text: string
  titlte_color: string
  status_text: string
  status_color: string
  trip_id: number
  type: number
  is_schedule: boolean
  redirect_to: string
  time: string
  image: string
  is_finding_driver: boolean
  items: unknown[]
}

export interface Invoice {
  company_address: string
  company_name: string
  company_tax_no: string
  email_receiver: string
  user_id: number
  id: number
}

// ===== Ride request =====
export interface V1RequestRideBody {
  latitude?: number
  longitude?: number
  current_latitude: number
  current_longitude: number
  customer_fare: number
  customer_fare_factor: number
  driver_fare_factor: number
  drop_location_address: string
  drop_location_address_name: string
  drop_location_address_label?: string
  duplicate_flag: number
  fare: number
  is_befar: number
  is_new_fare: number
  is_self_payment: number
  map_metadata: {
    pick_up: string
    pick_up_place_id?: string
    drop_off: string
    drop_off_place_id?: string
  }
  op_drop_latitude: number
  op_drop_longitude: number
  op_drop_place_id?: string
  pickup_location_address: string
  pickup_location_address_name: string
  request_location_address_label?: string
  reverse_bid: number
  vehicle_type: number
  location_accuracy: number
  region_id: number
  coupon_id?: number
  preferred_payment_mode?: number
  insurance_fee?: number
  insurance_provider?: number
  enable_insurance?: number
  invoice_id?: number
  customer_note?: string
  promo_to_apply?: number
  master_coupon?: number
  business_id?: string
  card_id?: string
  pickup_saved_address_id?: string
  dropoff_saved_address_id?: string
}

export interface V1RequestRideResponse {
  flag: number
  log: string
  message: string
  order_id: number
  session_id: number
  latitude: number
  longitude: number
  pickup_location_address: string
  drop_location_address: string
  start_time: string
  reverse_bid_time_interval: number
  html: string
  url: string
}

export async function v1CustomerFetchPointsOfInterest(body: {
  location: { latitude: number; longitude: number }
  latitude: number
  longitude: number
}): Promise<{ data: PointOfInterest[]; flag: number }> {
  const resp = await httpPost<{ data: PointOfInterest[]; flag: number }>(
    "be-autos/customer/fetch_points_of_interest",
    body
  )
  return resp.data
}

export interface V1FindNearbyDriversBody {
  request_latitude: number | string
  request_longitude: number | string
  ride_type: number | string
  include_poi: number
  is_show_all?: string
}

export async function v1FindNearbyDrivers(
  body: V1FindNearbyDriversBody
): Promise<NearbyDriversResponse> {
  const resp = await httpPost<NearbyDriversResponse>(
    "be-autos/prebooking/customer/v1/find_nearby_drivers",
    body
  )
  return resp.data
}

// ===== Request ride status =====
export interface V1RequestRideStatusBody {
  session_id: string
  preferred_payment_mode?: number
  card_id?: string
  booking_service_type?: number
}

export interface V1RequestRideStatusResponse {
  flag: number
  log: string
  session_id: number
  order_id: number
  latitude: number
  longitude: number
  op_drop_latitude: number
  op_drop_longitude: number
  start_time: string
}

export interface V1RequestRideStatusWithDriverResponse {
  engagement_id: number
  accept_time: string
  vehicle_type: number
  note?: unknown
  applicable_promo_id: number
  applicable_coupon_id: number
  session_id: number
  user_id: number
  driver_id: number
  status: number
  sub_region_id: number
  ride_type: number
  city: number
  pickup_time?: unknown
  pickup_latitude: number
  pickup_longitude: number
  pickup_location_address: string
  drop_latitude: number
  drop_longitude: number
  drop_location_address: string
  accept_latitude: number
  accept_longitude: number
  tip_amount: number
  distance: number
  ride_time: number
  fare: number
  pool_fare_customer_discount: number
  is_dp_automated: number
  request_latitude: number
  request_longitude: number
  updated_at: string
  preferred_payment_mode: number
  driver_in_perfect_ride: boolean
  engagement_updated_at: string
  op_drop_latitude: number
  op_drop_longitude: number
  in_perfect_ride: number
  insurance_status?: unknown
  insurance_fee?: unknown
  insurance_cert_url?: unknown
  app_versioncode: number
  device_type: number
  total_tip_amount?: unknown
  pretip_amount?: unknown
  request_e_invoiced: number
  start_time: string
  city_id: number
  number_masking_enabled: number
  chat_enabled: number
  pickup_geofence_id: number
  dropoff_geofence_id: number
  banner?: string
  perfect_latitude: number
  perfect_longitude: number
  final_fare_exclude_intrip_tip: number
  customer_fare: number
  free_ride: number
  fare_factor: number
  fare_flat_surge: number
  fare_details: {
    id: number
    is_applicable: number
    ride_type: number
    night_fare_applicable: number
    fare_fixed: number
    fare_per_km: number
    fare_threshold_distance: number
    fare_per_min: number
    fare_per_km_threshold_distance: number
    fare_per_km_after_threshold: number
    fare_per_km_before_threshold: number
    fare_threshold_time: number
    fare_per_waiting_min: number
    fare_threshold_waiting_time: number
    per_ride_driver_subsidy: number
    accept_subsidy_per_km: number
    fare_minimum: number
    accept_subsidy_threshold_distance: number
    accept_subsidy_threshold_time: number
    accept_subsidy_per_min: number
    cancellation_charges: number
    cancel_threshold_time: number
    cancel_threshold_distance: number
    accept_subsidy_before_threshold: number
    accept_subsidy_after_threshold: number
    fare_per_baggage: number
    tax_percentage: number
    type: number
    city: number
    operator_id: number
    business_id: number
    vehicle_type: number
    start_time: string
    end_time: string
    created_at: string
    updated_at: string
    fare_per_time_unit: number
    fare_per_night: number
    time_unit: number
    return_factor: number
    fare_stop_point_charge: number
    fare_change_destination: number
    scheduled_trip_fee: number
    scheduled_surge_cap: number
    schedule_night_time_config: string
    fuel_fee_cfg: string
    toggle_name: string
    display_base_fare: string
  }
  eta: number
  icon_set: string
  marker_icon: string
  images: {
    active_session?: string
    tab_normal?: string
    tab_highlighted?: string
    trip_detail_icon?: string
    invoice_icon?: string
    marker_icon?: string
    schedule_highlighted?: string
  }
  order_id: number
  is_pooled: number
  pickup_location_address_name: string
  drop_location_address_name: string
  pickup_datetime?: unknown
  status_version: number
  user_home_address: string
  booking_service_type: number
  is_scheduled_booking: number
  free_call_available: boolean
  cooperative_name: string
  is_recommend_save_trip: boolean
  is_pro_driver: boolean
  is_location_of_interest: boolean
  user_name: string
  phone_no: string
  user_image: string
  driver_car_no: string
  rating: number
  driver_car_image: string
  current_location_latitude: number
  current_location_longitude: number
  allow_change_destination: boolean
  vehicle_name: string
  enable_toggle_change_pickup: boolean
  vehicle_info: { model: string; brand: string; num_seats?: number }
  billboard?: unknown[]
  insurance_ui?: { title: string; text: string }
  remaining_change_pickup_times?: string
  have_changed_pickup?: boolean
  airport_guideline?: string
  flag: number
}

export async function v1RequestRideStatus(
  body: V1RequestRideStatusBody,
  options?: { formUrlEncoded?: boolean }
): Promise<
  V1RequestRideStatusResponse | V1RequestRideStatusWithDriverResponse
> {
  const isForm = Boolean(options?.formUrlEncoded)
  const data = isForm ? qs.stringify(body) : body
  const headers = isForm
    ? { "Content-Type": "application/x-www-form-urlencoded" }
    : { "Content-Type": "application/json" }
  const resp = await httpPost<
    V1RequestRideStatusResponse | V1RequestRideStatusWithDriverResponse
  >("be-autos/dispatch/get_request_ride_status", data, { headers })
  return resp.data
}

export async function v1RequestRide(
  body: V1RequestRideBody,
  options?: { formUrlEncoded?: boolean }
): Promise<V1RequestRideResponse> {
  const isForm = Boolean(options?.formUrlEncoded)
  const data = isForm ? qs.stringify(body) : body
  const headers = isForm
    ? { "Content-Type": "application/x-www-form-urlencoded" }
    : { "Content-Type": "application/json" }
  const resp = await httpPost<V1RequestRideResponse>(
    "be-autos/request_ride",
    data,
    { headers, timeout: 20000 }
  )
  return resp.data
}

export interface V1CancelRideRequestBody {
  cancelled_by: string
  session_id: string
}

export async function v1CancelRideRequest(body: V1CancelRideRequestBody) {
  const resp = await httpPost("be-autos/cancel_the_request", body)
  return resp.data
}

export interface V1CancelRideByCustomerBody {
  engagement_id: string
  reason_key: string
  reasons: string
}

export async function v1CancelRideByCustomer(body: V1CancelRideByCustomerBody) {
  const resp = await httpPost("be-autos/cancel_ride_by_customer", body)
  return resp.data
}

export interface V1GetRideSummaryBody {
  engagement_id: string
  session_id?: string
  order_history?: number
  show_right_menu?: number
}

export interface V1GetRideSummaryResponse {
  flag: number
  engagement_id: number
  user_id: number
  driver_id: number
  customer_cancellation_charges: number
  distance_old: number
  pickup_address: string
  drop_address: string
  actual_drop_address: string
  actual_distance: number
  pickup_time: string
  drop_time: string
  cancellation_charges: number
  ride_date: string
  vehicle_type: number
  fare: number
  discount_value: number
  paid_using_wallet: number
  paid_using_paytm: number
  preferred_payment_mode: number
  to_pay: number
  wait_time: number
  drop_timestamp: number
  ride_type: number
  accept_time: string
  request_made_on: string
  engagement_date: string
  status: number
  paid_using_stripe: number
  waiting_charges_applicable: number
  debt_added: number
  convenience_charge: number
  sub_region_id: number
  pool_fare_id: number
  customer_fare_id: number
  driver_fare_id: number
  paid_using_razorpay: number
  request_latitude: number
  request_longitude: number
  op_drop_latitude: number
  op_drop_longitude: number
  city: number
  ride_end_time: string
  driver_rating: string
  manually_edited: {
    text: string
    background_color: string
    text_color: string
  }
  fare_discount: number
  currency: string
  distance_unit: string
  utc_offset: string
  toll_charge: number
  tip_amount: number
  luggage_count: number
  customer_fare_per_baggage: number
  customer_tax_percentage: number
  paid_using_corporate: number
  distance: number
  ride_time: number
  request_e_invoiced: number
  scheduled_for?: unknown
  insurance_fee?: unknown
  insurance_cert_url?: unknown
  metadata: string
  total_tip_amount?: unknown
  pretip_amount?: unknown
  pretip_in_fare: number
  pickup_location_address_name: string
  drop_location_address_name: string
  is_self_payment: boolean
  drop_datetime: string
  pretip_exist: boolean
  final_fare_exclude_intrip_tip: number
  luggage_charges: number
  total_luggage_charges: number
  is_pooled: number
  trip_total: number
  driver_name: string
  driver_car_no: string
  discount: { key: string; value: number }[]
  driver_image: string
  phone_no: string
  driver_phone_no: string
  vehicle_info: { model: string; brand: string; color?: string }
  icon_set: string
  invoice_icon: string
  images: {
    active_session: string
    tab_normal: string
    tab_highlighted: string
    trip_detail_icon: string
    invoice_icon: string
    marker_icon: string
    schedule_highlighted: string
  }
  vehicle_name: string
  pickup_geofence_id: number
  dropoff_geofence_id: number
  pickup_datetime: string
  is_pro_driver: boolean
  jugnoo_balance: number
  last_4: string
  debt_money: number
}

export async function v1GetRideSummary(
  body: V1GetRideSummaryBody
): Promise<V1GetRideSummaryResponse> {
  const resp = await httpPost<V1GetRideSummaryResponse>(
    "be-autos/get_ride_summary",
    body
  )
  return resp.data
}

export interface V1SkipRatingByCustomerBody {
  engagement_id: string
}

export interface V1SkipRatingByCustomerResponse {
  flag: number
  log: string
}

export async function v1SkipRatingByCustomer(
  body: V1SkipRatingByCustomerBody
): Promise<V1SkipRatingByCustomerResponse> {
  const resp = await httpPost<V1SkipRatingByCustomerResponse>(
    "be-autos/skip_rating_by_customer",
    body
  )
  return resp.data
}

export interface V1FetchRatingTripsResponse {
  flag: number
  ratings: Array<{
    engagement_id: number
    session_id: number
    driver_id: number
    pickup_address: string
    drop_address: string
    wait_time: number
    accept_time: string
    op_drop_latitude: number
    op_drop_longitude: number
    request_latitude: number
    request_longitude: number
    pickup_time: string
    drop_time: string
    ride_end_time: string
    fare: number
    paid_using_wallet: number
    paid_using_paytm: number
    to_pay: number
    convenience_charge: number
    waiting_charges_applicable: number
    vehicle_type: number
    manually_edited: number
    sub_region_id: number
    ride_type: number
    pool_fare_id: number
    paid_using_stripe: number
    distance: number
    actual_fare: number
    paid_using_razorpay: number
    preferred_payment_mode: number
    city: number
    tip_amount: number
    toll_charge: number
    luggage_count: number
    customer_fare_per_baggage: number
    customer_tax_percentage: number
    paid_using_corporate: number
    ride_time: number
    request_e_invoiced: number
    pretip_amount?: unknown
    pretip_in_fare: number
    total_tip_amount?: unknown
    rate_app: number
    rate_app_dialog_content: {
      title: string
      text: string
      confirm_button_text: string
      cancel_button_text: string
      url: string
      never_button_text: string
    }
    pickup_geofence_id: number
    dropoff_geofence_id: number
    banner: string
    coupon: string
    promotion: string
    base_fare: number
    fare_factor: number
    icon_set: string
    invoice_icon: string
    is_pooled: number
    luggage_charges: number
    user_home_address: string
    loyalty_point: number
    pretip_exist: boolean
    final_fare_exclude_intrip_tip: number
    drop_location_address_name: string
    pickup_location_address_name: string
    pickup_datetime: string
    drop_datetime: string
    // NOTE: original schema sometimes contains a malformed key like "can rate driver".
    // We omit it here as it's not a valid identifier and not used by clients.
    driver_name: string
    driver_car_no: string
    vehicle_name: string
    total_luggage_charges: number
    discount: { key: string; value: number }[]
    currency: string
    emergency_no: string
    distance_unit: string
    debt_money: number
    is_pro_driver: boolean
    driver_info: {
      id: number
      name: string
      user_image: string
      phone_no: string
      driver_car_no: string
      vehicle_type: number
      bearing: number
      rating: number
      driver_car_image: string
      vehicle_info: { model: string; brand: string }
    }
    type: number
    addn_component: {
      icon: string
      title: string
      content: string
      background_color: string
      link: string
      campaign_name: string
      border_color: string
    }
    can_rate_driver: boolean
  }>
}

export async function v1FetchRatingTrips(): Promise<V1FetchRatingTripsResponse> {
  const resp = await httpPost<V1FetchRatingTripsResponse>(
    "be-autos/be_trip/fetch_rating_trips",
    {}
  )
  return resp.data
}

export async function v1FetchActiveSessions(
  body: Record<string, never>
): Promise<{
  flag: number
  title: string
  sessions: Session[]
  active_session_count: number
  available_services: number
  cancelled_bookings: unknown[]
  delivery_cancelled_bookings: unknown[]
  food_active_session_count: number
  active_transport_session_count: number
  has_active_sessions: boolean
  pending_group_orders: unknown[]
}> {
  const resp = await httpPost("be-autos/be_trip/fetch_active_sessions", body)
  return resp.data
}

export async function v1CreateInvoice(body: {
  invoice: Omit<Invoice, "id"> & { id?: number }
}): Promise<{ flag: number; invoice: Invoice }> {
  const resp = await httpPost<{ flag: number; invoice: Invoice }>(
    "be-autos/customer/create_invoice",
    body
  )
  return resp.data
}

export async function v1EditInvoice(body: {
  invoice: Omit<Invoice, "id"> & { id?: number }
}): Promise<{ flag: number; invoices: Invoice[] }> {
  const resp = await httpPost<{ flag: number; invoices: Invoice[] }>(
    "be-autos/customer/edit_invoice",
    body
  )
  return resp.data
}

export async function v1SetLocale(body: {
  updated_locale: string
}): Promise<{ flag: number; message: string; locale: string }> {
  const resp = await httpPost<{
    flag: number
    message: string
    locale: string
  }>("be-autos/customer/set_current_locale", body, {
    headers: { "Content-Type": "application/json", Charset: "UTF-8" },
  })
  return resp.data
}

export async function v1UpdateUserProfile(body: {
  signup_onboarding: number
  updated_user_email: string
  updated_user_name: string
  referral_code?: string
}): Promise<{ flag: number; error?: string; message?: string }> {
  const resp = await httpPost<{
    flag: number
    error?: string
    message?: string
  }>("be-autos/update_user_profile", body)
  return resp.data
}

export async function v1LogoutUser(body: { reason?: string }) {
  const resp = await httpPost("be-autos/logout_user", body)
  return resp.data
}

export async function v1FoodOrderTracking(body: {
  order_id: string
  ignore_user_check: boolean
}): Promise<v1FoodOrderTrackingResponse> {
  const resp = await httpPost<v1FoodOrderTrackingResponse>(
    "be-autos/food/order_tracking",
    body
  )
  return resp.data
}

export interface v1FoodOrderTrackingResponse {
  flag: number
  message: string
  status: number
  order_status: number
  delivery_status: number
  latitude: number
  longitude: number
  is_perfect_ride: boolean
  perfect_latitude: number
  perfect_longitude: number
  latest_log?: unknown
  bearing: number
  eta: number
  eda: number
  engagementStatus: number
  deliveries: unknown[]
  insurance: unknown
  is_live_tracking: boolean
  is_active: boolean
}

// This type mirrors the finding-driver status shape used by apps
export interface v2CustomerFindingDriverStatus {
  flag: number
  log: string
  session_id: number
  latitude: number
  longitude: number
  pickup_location_address: string
  pickup_location_address_name: string
  drop_location_address_name: string
  op_drop_latitude: number
  op_drop_longitude: number
  drop_location_address: string
  preferred_payment_mode: number
  is_pooled: number
  start_time: string
  bids: unknown[]
  emergency_no: string
  reverse_bid_time_interval: number
  isReDispatch: number
  vehicle_type: number
  allow_change_destination: boolean
  pros_enabled: number
  nearbyPickupRegions: unknown
  points_of_interest: unknown[]
  chat_enabled: boolean
}

export async function v2AddHomeAndWorkAddress(body: {
  address: string
  login_type: number
  is_confirmed: number
  latitude: number
  longitude: number
  type: string
  google_place_id: string
  keep_duplicate: number
}): Promise<{
  flag: number
  message: string
  id: number
  addresses: {
    addr: string
    instr: string
    lat: string
    lng: string
    freq: number
    type: string
    id: number
    google_place_id: string
    is_confirmed: number
  }[]
}> {
  const resp = await httpPost("be-autos/v2/add_home_and_work_address", body)
  return resp.data
}

interface UserSavedAddress {
  id: number
  address: string
  google_place_id: string
  type: string
  latitude: number
  longitude: number
  is_confirmed: number
  instructions?: unknown
  lat?: string
  lng?: string
}

export async function v1FetchUserAddress(): Promise<{
  flag: number
  message: string
  addresses: UserSavedAddress[]
}> {
  const resp = await httpPost<{
    flag: number
    message: string
    addresses: UserSavedAddress[]
  }>("be-autos/customer/fetch_user_address", {})
  return resp.data
}

export interface V1ConfigurationResponse {
  flag: number
  configs: Configs
}

export interface Confirmbookingmessage {
  time_triggered_in_second: number
  title: string
  description: string
}

export interface Localeset {
  locale: string
  name: string
}

export interface Cancellation {
  message: string
  reasons: string[]
  addn_reason: string
}

export interface Configs {
  cancellation: Cancellation
  auto_enable_transport_insurance_time: number
  bad_rating_reasons: string[]
  average_rating_reasons: string[]
  good_rating_reasons: string[]
  jugnoo_cash_tnc: string
  user_app_monitoring_duration: string
  side_menu_mods: string
  fb_share_image: string
  invite_earn_short_msg_referral_only: string
  invite_earn_more_info_referral_only: string
  jugnoo_sticky: number
  user_app_monitoring: string
  in_app_support_panel_version: string
  invite_earn_screen_image_android: string
  invite_earn_screen_image_ios: string
  show_in_account: string
  customer_location_update_interval: string
  app_open_push_threshold_time: string
  refer_all_contacts_title_odd: string
  refer_all_contacts_title_even: string
  refer_all_contacts_text_odd: string
  refer_all_contacts_text_even: string
  refer_all_contacts_text_login_odd: string
  refer_all_contacts_text_login_even: string
  refer_all_contacts_title_login_even: string
  refer_all_contacts_title_login_odd: string
  login_popup_title: string
  login_popup_message: string
  login_popup_image: string
  login_popup_button_text: string
  login_popup_enabled: string
  login_popup_deepindex: string
  referral_leaderboard_enabled: string
  login_popup_cities: string
  login_popup_et: string
  login_popup_st: string
  cancellation_charges_popup_text_line2: string
  show_offer_dialog: number
  referral_activity_enabled: string
  destination_help_text_enable: string
  destination_help_text: string
  ride_summary_text: string
  in_ride_send_invite_text_bold: string
  in_ride_send_invite_text_normal: string
  confirm_screen_fare_estimate_enabled: string
  use_recent_loc_user_list: string
  cancellation_charges_popup_text_line1: string
  pool_destination_popup_text1: string
  pool_destination_popup_text2: string
  use_recent_loc_auto_snap_min_distance: number
  pool_destination_popup_text3: string
  invite_friend_button: string
  ride_end_good_feedback_view_type: string
  ride_end_good_feedback_text: string
  base_fare_pool_text: string
  notification_preference_enabled: string
  use_recent_loc_at_request: number
  use_recent_loc_auto_snap_max_distance: number
  fab_button_enabled: number
  update_app_to_jugnoo: number
  force_update_fresh_to_auto: number
  ride_start_invite_text_generic: string
  in_ride_driver_customer_text_normal: string
  in_ride_driver_customer_text_bold: string
  in_ride_driver_customer_text_deep_index: string
  pod_service: string
  chat_info: string
  facebook_page_id: string
  facebook_page_url: string
  show_about: number
  ride_feedback_rating_bar: number
  show_promo: number
  default_lang: string
  show_skip_onboarding: number
  support_email_subject: string
  cust_side_menu_offers: string
  cust_side_menu_inbox: string
  cust_side_menu_history: string
  cust_side_menu_promotions: string
  cust_side_menu_wallet: string
  tip_enabled: string
  cust_side_menu_address_book: string
  customer_fetch_inride_path_interval: number
  customer_fetch_driver_location_interval: number
  delivery_term_and_conditions_link: string
  scheduled_booking_max_bookings_per_day: number
  scheduled_booking_min_scheduled_time_after_in_minutes: number
  scheduled_booking_max_scheduled_time_after_in_days: number
  scheduled_booking_scheduled_time_increment_in_minutes: number
  app_polling_interval: number
  app_request_ride_timeout: number
  auto_suggest_radius: number
  app_delivery_tracking_accepted_interval: number
  app_delivery_tracking_started_interval: number
  autocomplete_delay_time: string
  tracking_maximum_retention_time: string
  tracking_sending_interval_time: string
  tracking_platforms: string
  tracking_use_stream: string
  tracking_enable: boolean
  bounce_dispatch_after_seconds: number
  bounce_dispatch_enabled: boolean
  maximum_radius_to_change_pickup_location: number
  limit_point_of_interests_before_hiding: number
  max_saved_address: number
  login_expired_time_in_seconds: number
  app_update_location_time: number
  app_back_to_home_screen_time: number
  average_rating_reasons_for_bike: string[]
  merchant_1star_rating_reasons: string[]
  merchant_2stars_rating_reasons: string[]
  merchant_3stars_rating_reasons: string[]
  merchant_4stars_rating_reasons: string[]
  merchant_5stars_rating_reasons: string[]
  driver_food_1star_rating_reasons: string[]
  driver_food_2stars_rating_reasons: string[]
  driver_food_3stars_rating_reasons: string[]
  driver_food_4stars_rating_reasons: string[]
  driver_food_5stars_rating_reasons: string[]
  betix_enable: boolean
  app_to_map_enable: boolean
  scheduled_booking_enabled: boolean
  enable_remove_long_trip_tab: boolean
  enable_suggest_schedule_when_befar_timeout: boolean
  enable_food_schedule_booking: boolean
  confirm_booking_messages: Confirmbookingmessage[]
  locale_set: Localeset[]
  car_vehicle_types: number[]
  bike_vehicle_types: number[]
}

export async function v1Configuration(
  body: Record<string, unknown>
): Promise<V1ConfigurationResponse> {
  const resp = await httpPost<V1ConfigurationResponse>(
    "be-autos/get_configuration",
    body
  )
  return resp.data
}

export interface V1GetDriverCurrentLocationBody {
  driver_id: number
  engagement_id: number
}

export interface V1GetDriverCurrentLocationResponse {
  flag: number
  latitude: number
  longitude: number
  eta: number
  bearing: number
}

export async function v1GetDriverCurrentLocation(
  body: V1GetDriverCurrentLocationBody
): Promise<V1GetDriverCurrentLocationResponse> {
  const resp = await httpPost<V1GetDriverCurrentLocationResponse>(
    "be-autos/get_driver_current_location",
    body
  )
  return resp.data
}

export interface V2CustomerGetCurrentUserStatusBody {
  session_id?: number
  status_version?: number
  engagement_id?: number
}

export interface V2CustomerGetCurrentUserStatusResponse {
  flag: number
  last_engagement_info: FullEngagementInfo[]
  last_ride: unknown
  fare_factor: number
  pros_enabled: number
  nearbyPickupRegions: unknown
  points_of_interest: unknown[]
  chat_enabled: boolean
}

export async function v2CustomerGetCurrentUserStatus(
  body: V2CustomerGetCurrentUserStatusBody
): Promise<V2CustomerGetCurrentUserStatusResponse> {
  const resp = await httpPost<V2CustomerGetCurrentUserStatusResponse>(
    "be-autos/v2/customer/get_current_user_status",
    body
  )
  return resp.data
}

export interface V1RateDriverBody {
  engagement_id: string
  feedback: string
  feedback_reasons: string
  given_rating: number
  driver_id: string
  access_token: string
}

export async function v1RateDriver(
  body: V1RateDriverBody
): Promise<{ flag: number; message?: string }> {
  const resp = await httpPost<{ flag: number; message?: string }>(
    "be-autos/rate_the_driver",
    body
  )
  return resp.data
}

export async function v1GetRegions(
  body: Record<string, never>
): Promise<{ flag: number; regions: RegionExtended[] }> {
  const resp = await httpPost<{ flag: number; regions: RegionExtended[] }>(
    "be-autos/get_regions",
    body
  )
  return resp.data
}
